import { compose, mapProps, withProps } from 'recompose';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import type { EnrollmentChoices } from 'bundles/enroll/components/xdp/withEnrollment';
import Naptime from 'bundles/naptimejs';
import ProductSkuV1 from 'bundles/naptimejs/resources/productSkus.v1';
import S12nDerivativesV1 from 'bundles/naptimejs/resources/s12nDerivatives.v1';
import {
  isOwnableProductsEnabled,
  withFindOwnableProductsByUnderlyingProductItemId,
} from 'bundles/payments-common/hooks/useFindOwnableProducts';
import type { PropsFromFindByUnderlyingProductItemId } from 'bundles/payments-common/hooks/useFindOwnableProducts';
import { CREDENTIAL_TRACK_SUBSCRIPTION } from 'bundles/payments/common/ProductType';

export type PropsToWithPrice = {
  enrollmentAvailableChoices?: EnrollmentChoices;
  s12nId?: string;
};

type PropsFromWithPriceNaptime = {
  s12nDerivative?: S12nDerivativesV1;
  multiS12nDerivatives?: Array<S12nDerivativesV1>;
};

export type PropsFromWithPrice = PropsFromWithPriceNaptime;

const withPrice = <InputProps extends PropsToWithPrice>(isNotRequired?: boolean) =>
  compose<PropsFromWithPriceNaptime, InputProps>(
    Naptime.createContainer<PropsFromWithPriceNaptime, InputProps>(({ enrollmentAvailableChoices, s12nId }) => {
      if (!enrollmentAvailableChoices) {
        return {};
      }
      // if user can subscribe to multiple s12ns but can't do free trial for any s12n,
      // retrieve the price for every s12n
      if (
        enrollmentAvailableChoices.isMixAndMatch &&
        !enrollmentAvailableChoices.canEnrollThroughS12nSubscriptionFreeTrial
      ) {
        const enrollmentS12nIds = enrollmentAvailableChoices.subscriptionEnrollmentS12nIds;

        return {
          multiS12nDerivatives: S12nDerivativesV1.multiGet(enrollmentS12nIds, {
            fields: ['catalogPrice'],
            required: !isNotRequired,
          }),
        };
      } else if (enrollmentAvailableChoices.canSubscribeToS12n) {
        return {
          // If we can subscribe to the s12n, it follows that we have a s12nId.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          s12nDerivative: S12nDerivativesV1.get(s12nId!, {
            fields: ['catalogPrice'],
            required: !isNotRequired,
          }),
        };
      } else {
        return {
          productPrices: [],
        };
      }
    }),
    mapProps<PropsFromWithPrice, PropsFromWithPriceNaptime>(({ multiS12nDerivatives, ...rest }) => ({
      ...(multiS12nDerivatives && {
        multiS12nDerivatives,
        s12nDerivative: multiS12nDerivatives[0],
      }),
      ...rest,
    }))
  );

type PropsToWithCredentialTrackProductSkus = {
  id: string;
};

export type ProductSku = {
  id: string;
  underlyingProductId?: string;
  productType: string;
  numberOfCycles?: number | null;
  subscriptionType?: string | null;
};

export type PropsFromWithCredentialTrackProductSkus = {
  productSkus?: ProductSku[];
};

type PropsFromWithProps = { underlyingProductItemId?: string };

type PropsFromProductSkusNaptime = {
  productSkus?: ProductSkuV1[];
};

const withCredentialTrackProductSKUs = <InputProps extends PropsToWithCredentialTrackProductSkus>() =>
  compose<PropsFromWithCredentialTrackProductSkus, InputProps>(
    // This underlyingProductItemId is only used to get passed to `withFindOwnableProductsByUnderlyingProductItemId`
    withProps<PropsFromWithProps, InputProps>(({ id }) => {
      if (isOwnableProductsEnabled()) {
        return { underlyingProductItemId: id };
      }

      return {};
    }),
    withFindOwnableProductsByUnderlyingProductItemId,
    Naptime.createContainer<PropsFromWithCredentialTrackProductSkus, InputProps>(({ id }) => {
      if (!isOwnableProductsEnabled() && id) {
        return {
          productSkus: ProductSkuV1.finder('findByUnderlying', {
            params: {
              id: tupleToStringKey([CREDENTIAL_TRACK_SUBSCRIPTION, id]),
            },
            fields: ['id', 'productType', 'properties'],
          }),
        };
      } else {
        return {};
      }
    }),
    mapProps<
      PropsFromWithCredentialTrackProductSkus,
      PropsFromWithProps & PropsFromFindByUnderlyingProductItemId & PropsFromProductSkusNaptime
      // Destructure `underlyingProductItemId` so it doesn't returned back to the consuming component
    >(({ ownableProducts, productSkus, underlyingProductItemId, ...props }) => {
      const formattedOwnableProducts = ownableProducts?.length
        ? ownableProducts?.map(({ productType, productItemId, numberOfInstallments, ...rest }) => {
            return {
              id: tupleToStringKey([productType ?? '', productItemId ?? '']),
              productType: productType ?? '',
              numberOfCycles: numberOfInstallments,
              underlyingProductId: tupleToStringKey([CREDENTIAL_TRACK_SUBSCRIPTION, underlyingProductItemId ?? '']),
              ...rest,
            };
          })
        : undefined;

      const sortedData = (formattedOwnableProducts ?? productSkus)?.sort((skuA, skuB) => {
        // Need to set a number by default because 'properties' is possibly null/undefined
        const cyclesA = skuA?.numberOfCycles ?? 0;
        const cyclesB = skuB?.numberOfCycles ?? 0;
        return cyclesA - cyclesB;
      });

      return {
        ...props,
        productSkus: sortedData,
      };
    })
  );

export { withPrice, withCredentialTrackProductSKUs };
